/**
 * 简化的菜单处理器
 * 专门用于解决菜单重复显示问题
 */

import { MenuItem } from '../types/menu';

export interface ProcessedMenuItem extends MenuItem {
  key: string;
  level: number;
  hasChildren: boolean;
}

/**
 * 处理菜单数据，构建正确的树形结构
 */
export const processMenuData = (menus: MenuItem[]): ProcessedMenuItem[] => {
  // 第一步：严格去重
  const uniqueMenus = Array.from(new Map(menus.map(menu => [menu.id, menu])).values());
  
  // 第二步：构建树形结构
  const menuMap = new Map<number, MenuItem & { children: MenuItem[] }>();
  const roots: (MenuItem & { children: MenuItem[] })[] = [];

  // 初始化所有节点
  uniqueMenus.forEach(menu => {
    menuMap.set(menu.id, { ...menu, children: [] });
  });

  // 构建父子关系
  uniqueMenus.forEach(menu => {
    const node = menuMap.get(menu.id)!;
    if (menu.parentId === 0) {
      // 根节点 - 检查是否已存在
      if (!roots.find(root => root.id === node.id)) {
        roots.push(node);
      }
    } else {
      // 子节点
      const parent = menuMap.get(menu.parentId);
      if (parent) {
        // 检查是否已存在
        if (!parent.children.find(child => child.id === node.id)) {
          parent.children.push(node);
        }
      }
    }
  });

  // 第三步：按orderNum排序
  const sortNodes = (nodes: (MenuItem & { children: MenuItem[] })[]) => {
    nodes.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));
    nodes.forEach(node => {
      if (node.children.length > 0) {
        sortNodes(node.children as (MenuItem & { children: MenuItem[] })[]);
      }
    });
  };

  sortNodes(roots);

  // 第四步：扁平化树形结构为表格数据
  const flattenTree = (nodes: (MenuItem & { children: MenuItem[] })[], level = 0): ProcessedMenuItem[] => {
    const result: ProcessedMenuItem[] = [];
    nodes.forEach(node => {
      result.push({
        ...node,
        key: node.id.toString(),
        level,
        hasChildren: node.children.length > 0,
      });
      // 递归处理子节点
      if (node.children.length > 0) {
        result.push(...flattenTree(node.children as (MenuItem & { children: MenuItem[] })[], level + 1));
      }
    });
    return result;
  };

  const processedData = flattenTree(roots);
  
  // 检查是否有重复数据（静默处理，不输出日志）
  const ids = processedData.map(item => item.id);
  const uniqueIds = Array.from(new Set(ids));
  if (ids.length !== uniqueIds.length) {
    // 发现重复数据，但不再输出调试信息
  }
  
  
  return processedData;
};

/**
 * 处理左侧菜单数据，构建完整的树形结构
 * 只显示有权限的菜单，如果父菜单没有子菜单（子菜单都被过滤），则也不显示父菜单
 */
export const processSidebarMenuData = (menus: MenuItem[]): MenuItem[] => {
  
  // 严格去重
  const uniqueMenus = Array.from(new Map(menus.map(menu => [menu.id, menu])).values());
  
  // 构建树形结构
  const menuMap = new Map<number, MenuItem & { children: MenuItem[] }>();
  const roots: (MenuItem & { children: MenuItem[] })[] = [];

  // 初始化所有节点
  uniqueMenus.forEach(menu => {
    menuMap.set(menu.id, { ...menu, children: [] });
  });

  // 构建父子关系
  uniqueMenus.forEach(menu => {
    const node = menuMap.get(menu.id)!;
    if (menu.parentId === 0) {
      // 根节点
      roots.push(node);
    } else {
      // 子节点
      const parent = menuMap.get(menu.parentId);
      if (parent) {
        parent.children.push(node);
      }
    }
  });

  // 过滤：移除没有子菜单的父菜单（如果父菜单本身不是叶子节点）
  // 但保留菜单类型为分类（M）的父菜单，即使没有子菜单
  const filterEmptyParents = (nodes: (MenuItem & { children: MenuItem[] })[]): (MenuItem & { children: MenuItem[] })[] => {
    const result: (MenuItem & { children: MenuItem[] })[] = [];
    
    for (const node of nodes) {
      // 递归处理子节点
      const filteredChildren = node.children.length > 0 
        ? filterEmptyParents(node.children as (MenuItem & { children: MenuItem[] })[])
        : [];
      
      // 如果节点有子菜单，保留过滤后的子菜单
      if (filteredChildren.length > 0) {
        result.push({ ...node, children: filteredChildren });
        continue;
      }
      
      // 如果节点没有子菜单，检查是否是叶子节点（menuType 为 'C' 或 'F'）
      // 如果是叶子节点，保留；如果是分类节点（'M'）但没有子菜单，移除
      if (node.menuType === 'C' || node.menuType === 'F' || !node.menuType) {
        // 叶子节点，保留
        result.push({ ...node, children: [] });
      }
      // 分类节点但没有子菜单，不添加到结果中（移除）
    }
    
    return result;
  };

  const filteredRoots = filterEmptyParents(roots);

  // 按orderNum排序
  const sortNodes = (nodes: (MenuItem & { children: MenuItem[] })[]) => {
    nodes.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));
    nodes.forEach(node => {
      if (node.children.length > 0) {
        sortNodes(node.children as (MenuItem & { children: MenuItem[] })[]);
      }
    });
  };

  sortNodes(filteredRoots);
  
  return filteredRoots;
};
